import { AgGridModule } from '@ag-grid-community/angular';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import {
  MatDialog,
  MaterialLibraryModule,
} from '@cdw-ae/common-frontend-materials';
import { CdwIconModule } from '@cdw-ae/common-ui-components-icon';
import { CdwSearchGridModule } from '@cdw-ae/common-ui-components-search-grid';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import * as FilterActions from '../../../../+state/filter/filter.actions';
import { RootState } from '../../../../common/interfaces/rootState.interface';
import { GoogleTagManagerService } from '../../../../common/services/analytics/google-tag-manager.service';
import { DevicesDataService } from '../../../../common/services/device/devices-data.service';
import { UsersDataService } from '../administration/access-management/users/users-data.service';
import { GenericSearchPanelComponent } from './generic-search-panel/generic-search-panel.component';
import { UserNameMatch } from './generic-search-panel/generic-search-panel.model';
import { ReportData } from './report-viewer/report-viewer.model';
import {
  DeviceSearchByOption,
  REPORT_TYPES,
  ReportPanelType,
  UserSearchByOption,
} from './reports.model';

@Component({
  selector: 'reports',
  standalone: true,
  imports: [
    CommonModule,
    AgGridModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MaterialLibraryModule,
    CdwIconModule,
    CdwSearchGridModule,
  ],
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
})
export class ReportsComponent implements OnInit {
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private store = inject(Store<RootState>);
  private devicesDataService = inject(DevicesDataService);
  private usersDataService = inject(UsersDataService);
  private gtmService = inject(GoogleTagManagerService);
  private dialogIsOpen = false;

  // Expose report type constants for template usage
  public readonly reportTypes = REPORT_TYPES;

  ngOnInit(): void {
    // Check if we navigated here with state to auto-open a panel
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state ?? window.history.state;

    const panelType = state?.openPanel || state?.workflowType;
    if (panelType) {
      this.handleOpenPanel(panelType);

      // Clear the state to prevent re-opening when navigating back
      window.history.replaceState({}, document.title);
    }
  }

  private handleOpenPanel(panelType: ReportPanelType): void {
    switch (panelType) {
      case ReportPanelType.SINGLE_DEVICE:
        this.openSingleDeviceSearchPanel();
        break;
      case ReportPanelType.SINGLE_USER:
        this.openSingleUserSearchPanel();
        break;
      case ReportPanelType.MODEL_AUE_BY_OU:
        this.openReport(REPORT_TYPES.MODEL_AUE);
        break;
      case ReportPanelType.FIFTEEN_DAY_ACTIVITY_BY_OU:
        this.openReport(REPORT_TYPES.FIFTEEN_DAY_ACTIVITY);
        break;
      case ReportPanelType.CHROME_VERSION_AGE_BY_OU:
        this.openReport(REPORT_TYPES.CHROME_VERSION_AGE);
        break;
      case ReportPanelType.BATTERY_HEALTH_BY_OU:
        this.openReport(REPORT_TYPES.BATTERY_HEALTH);
        break;
      case ReportPanelType.STORAGE_BY_OU:
        this.openReport(REPORT_TYPES.STORAGE_BY_OU);
        break;
    }
  }

  public openSingleDeviceSearchPanel(): void {
    if (this.dialogIsOpen) {
      return; // Prevent opening multiple dialogs
    }

    this.dialogIsOpen = true;
    const dialogRef = this.dialog.open(GenericSearchPanelComponent, {
      width: '800px',
      height: '400px',
      position: { bottom: '0px' },
      panelClass: 'bottom-panel-dialog',
      disableClose: false,
      hasBackdrop: true,
      data: {
        title: 'Single Device Report - Search',
        searchType: 'device',
        onSearch: async (
          searchBy: DeviceSearchByOption,
          searchValue: string
        ) => {
          return await this.performDeviceSearch(searchBy, searchValue);
        },
      },
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.dialogIsOpen = false; // Reset flag when dialog closes
      });
  }

  public openSingleUserSearchPanel(): void {
    if (this.dialogIsOpen) {
      return; // Prevent opening multiple dialogs
    }

    this.dialogIsOpen = true;
    const dialogRef = this.dialog.open(GenericSearchPanelComponent, {
      width: '800px',
      height: '425px',
      position: { bottom: '0px' },
      panelClass: 'bottom-panel-dialog',
      disableClose: false,
      hasBackdrop: true,
      data: {
        title: 'Single User Report - Search',
        searchType: 'user',
        onSearch: async (searchBy: UserSearchByOption, searchValue: string) => {
          return await this.performUserSearch(searchBy, searchValue);
        },
        onNameSearch: ({
          givenName,
          familyName,
          limit,
        }: {
          givenName?: string;
          familyName?: string;
          limit?: number;
        }) => {
          return this.usersDataService.searchUsersInDirectory({
            searchBy: 'name',
            givenName,
            familyName,
            limit,
          });
        },
        onSelectNameMatch: (match: UserNameMatch): void => {
          const reportData: ReportData = {
            type: 'SINGLE_USER',
            data: match,
          };
          this.router.navigate(['/report-viewer', 'SINGLE_USER'], {
            state: { reportData },
          });
        },
      },
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.dialogIsOpen = false; // Reset flag when dialog closes
      });
  }

  private performDeviceSearch(
    searchBy: DeviceSearchByOption,
    searchValue: string
  ): Promise<string | void> {
    return new Promise((resolve) => {
      // If searching by device ID, use direct lookup
      if (searchBy === DeviceSearchByOption.DEVICE_ID) {
        this.devicesDataService
          .getDeviceById(searchValue)
          .pipe(take(1))
          .subscribe({
            next: (device) => {
              if (device) {
                this.router.navigate(['/device-summary', device.id]);
                resolve(); // Success
              } else {
                resolve('No device found with that CDW Device ID.');
              }
            },
            error: () => {
              resolve('Failed to search for device. Please try again.');
            },
          });
        return;
      }

      // For serial number, asset ID, and annotated user, use filter-based search
      const fieldNameMap: Record<DeviceSearchByOption, string> = {
        [DeviceSearchByOption.SERIAL_NUMBER]: 'serialNumber',
        [DeviceSearchByOption.ASSET_ID]: 'annotatedAssetId',
        [DeviceSearchByOption.ANNOTATED_USER]: 'annotatedUser',
        [DeviceSearchByOption.DEVICE_ID]: 'id', // Won't be used due to early return above
      };

      const fieldName = fieldNameMap[searchBy];
      if (!fieldName) {
        resolve('Invalid search field selected.');
        return;
      }

      const displayNameMap: Record<DeviceSearchByOption, string> = {
        [DeviceSearchByOption.SERIAL_NUMBER]: 'Serial Number',
        [DeviceSearchByOption.ASSET_ID]: 'Asset ID',
        [DeviceSearchByOption.ANNOTATED_USER]: 'Annotated User',
        [DeviceSearchByOption.DEVICE_ID]: 'CDW Device ID',
      };

      // Create filter for exact match - structure must match SelectedFilterField interface
      const filter = {
        matchValue: searchValue,
        field: {
          displayName: displayNameMap[searchBy],
          name: fieldName,
          singleUse: false,
          fieldMatchers: [],
        },
        selectedMatcher: {
          name: 'fullMatch',
          displayName: 'Matches',
          hasEditor: true,
          options: {},
        },
        uid: `${fieldName}-${Date.now()}`,
      };

      // Search for device using the filter
      this.devicesDataService
        .getDevices({
          activeFilters: [filter],
          selectFields: ['id'],
        })
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            if (response.data && response.data.length === 1) {
              // Exactly one device found - navigate to device summary
              const deviceId = response.data[0].id;
              this.router.navigate(['/device-summary', deviceId]);
              resolve(); // Success
            } else if (response.data && response.data.length > 1) {
              // Multiple devices found (shouldn't happen with unique fields)
              resolve('Multiple devices found. Please refine your search.');
            } else {
              // No devices found
              resolve('No device found matching the search criteria.');
            }
          },
          error: () => {
            resolve('Failed to search for device. Please try again.');
          },
        });
    });
  }

  private performUserSearch(
    searchBy: UserSearchByOption,
    searchValue: string
  ): Promise<string | void> {
    return new Promise((resolve) => {
      this.usersDataService
        .searchUsersInDirectory({ searchBy: 'email', searchValue })
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            const matches = response.users ?? [];

            if (matches.length === 0) {
              resolve('No matches found. Please try a different search.');
            } else if (matches.length === 1) {
              const reportData: ReportData = {
                type: 'SINGLE_USER',
                data: matches[0],
              };
              this.router.navigate(['/report-viewer', 'SINGLE_USER'], {
                state: { reportData },
              });
              resolve();
            } else {
              resolve(
                `Found ${matches.length} matches. Please refine your search to get a single result.`
              );
            }
          },
          error: (err: HttpErrorResponse) => {
            const panelMessage =
              err.status === 400 && typeof err.error?.message === 'string'
                ? err.error.message
                : 'Failed to search for users. Please try again.';
            resolve(panelMessage);
          },
        });
    });
  }

  /**
   * Open an aggregate report by type
   */
  public openReport(reportType: string): void {
    // Track report "See Report" button click in GTM
    this.gtmService.pushUserAction({
      category: 'reports',
      action: 'see_report_clicked',
      metadata: {
        report_type: reportType,
      },
    });

    this.store.dispatch(
      FilterActions.updateFilters({
        identifier: reportType,
        clickedValue: 'default',
      })
    );
  }
}
